import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Palette, 
  Video, 
  TrendingUp, 
  Globe, 
  ArrowRight, 
  CheckCircle 
} from 'lucide-react';

const ScrollSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

const ServicesPage = () => {
  const services = [
    {
      id: 'branding',
      icon: Palette,
      title: 'Branding & Design',
      description: 'Strategic brand identity that makes you unforgettable. We create visual systems that communicate your value and build trust.',
      image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80',
      outcomes: [
        'Cohesive brand identity that resonates',
        'Visual systems that scale with your business',
        'Design that converts visitors to customers',
      ],
      deliverables: [
        'Logo & Identity Design',
        'Brand Guidelines',
        'Marketing Collateral',
        'Social Media Templates',
        'Pitch Decks & Presentations',
      ],
    },
    {
      id: 'video',
      icon: Video,
      title: 'Video Production',
      description: 'Cinematic storytelling that captures attention and drives action. From brand films to social content, we produce video that performs.',
      image: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800&q=80',
      outcomes: [
        'Higher engagement across platforms',
        'Stronger emotional connection with audience',
        'Content that drives conversions',
      ],
      deliverables: [
        'Brand Films & Documentaries',
        'Social Media Videos (Reels/Shorts)',
        'Product & Service Videos',
        'Testimonial Videos',
        'Event Coverage',
      ],
    },
    {
      id: 'marketing',
      icon: TrendingUp,
      title: 'Performance Marketing',
      description: 'Data-driven campaigns that deliver measurable ROI. We optimize relentlessly for leads, conversions, and revenue.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
      outcomes: [
        'Predictable lead generation',
        'Lower customer acquisition cost',
        'Clear attribution and ROI tracking',
      ],
      deliverables: [
        'Paid Social Campaigns (Meta, LinkedIn)',
        'Google Ads & Search Marketing',
        'Conversion Rate Optimization',
        'Analytics & Reporting',
        'Marketing Strategy & Planning',
      ],
    },
    {
      id: 'tech',
      icon: Globe,
      title: 'Website & Tech Solutions',
      description: 'Modern, conversion-optimized digital experiences. We build websites and platforms that work as hard as you do.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
      outcomes: [
        'Higher conversion rates',
        'Better user experience',
        'Scalable digital infrastructure',
      ],
      deliverables: [
        'Website Design & Development',
        'Landing Page Optimization',
        'E-commerce Solutions',
        'Custom Web Applications',
        'SEO & Technical Optimization',
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Our Services</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Comprehensive Solutions for{' '}
              <span className="text-gradient">Digital Growth</span>
            </h1>
            <p className="text-fp-textMuted text-lg leading-relaxed">
              From brand strategy to execution, we provide end-to-end services 
              designed to elevate your presence and accelerate your growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-32">
            {services.map((service, index) => (
              <ScrollSection key={service.id} delay={0.1}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                  index % 2 === 1 ? '' : ''
                }`}>
                  {/* Image */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="relative">
                      <div className="gradient-border p-1">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full aspect-[4/3] object-cover rounded-xl"
                        />
                      </div>
                      {/* Service Icon Badge */}
                      <div className="absolute -bottom-6 -right-6 w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg shadow-primary/30">
                        <service.icon className="w-10 h-10 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      {service.title}
                    </h2>
                    <p className="text-fp-textMuted text-lg leading-relaxed mb-8">
                      {service.description}
                    </p>

                    {/* Outcomes */}
                    <div className="mb-8">
                      <h3 className="text-sm font-semibold text-primary mb-4 uppercase tracking-wider">
                        What You Get
                      </h3>
                      <ul className="space-y-3">
                        {service.outcomes.map((outcome, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-fp-textMuted">{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Deliverables */}
                    <div className="mb-8">
                      <h3 className="text-sm font-semibold text-primary mb-4 uppercase tracking-wider">
                        Deliverables
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {service.deliverables.map((deliverable, i) => (
                          <span
                            key={i}
                            className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-fp-textMuted"
                          >
                            {deliverable}
                          </span>
                        ))}
                      </div>
                    </div>

                    <Link
                      to="/get-proposal"
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid={`service-cta-${service.id}`}
                    >
                      Get a Proposal
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </ScrollSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <ScrollSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Not Sure What You Need?
            </h2>
            <p className="text-fp-textMuted text-lg mb-10 max-w-2xl mx-auto">
              Book a free strategy call and we'll help you identify 
              the right services for your goals and budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book-call"
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="services-cta-call"
              >
                Book a Strategy Call
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/get-proposal"
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="services-cta-proposal"
              >
                Get a Proposal
              </Link>
            </div>
          </ScrollSection>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
